import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const ForgotPassword: React.FC = () => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error: resetError } = await resetPassword(email);

    if (resetError) {
      setError(resetError.message);
      setLoading(false);
    } else {
      setSuccess(true);
      setLoading(false);
    }
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate flex min-h-screen w-full items-center justify-center bg-white px-6 py-16">
        {/* Blueprint grid */}
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: EASE }}
          className="relative z-10 w-full max-w-sm"
        >
          {success ? (
            <>
              <div className="mb-8 text-center">
                <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Steelman
                </span>
                <h1 className="mt-3 font-serif text-4xl tracking-tight text-charcoal">
                  Check your email.
                </h1>
                <p className="mt-2 text-sm text-ink-muted">
                  A reset link is on its way.
                </p>
              </div>

              <div className="rounded-2xl border border-black/[0.07] bg-white/90 p-8 text-center shadow-frame backdrop-blur-sm">
                <p className="text-sm leading-relaxed text-ink-muted">
                  We sent a password reset link to{' '}
                  <span className="font-mono text-[13px] font-medium text-charcoal">{email}</span>.
                  Follow it to choose a new password.
                </p>

                <div className="my-6 h-px w-full bg-hairline" />

                <Link to="/signin" className="btn-secondary w-full">
                  Return to sign in
                </Link>

                <p className="mt-4 text-xs text-ink-muted">
                  Nothing arriving? Check your spam folder.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="mb-8 text-center">
                <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Steelman
                </span>
                <h1 className="mt-3 font-serif text-4xl tracking-tight text-charcoal">
                  Reset your password.
                </h1>
                <p className="mt-2 text-sm text-ink-muted">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>

              <div className="rounded-2xl border border-black/[0.07] bg-white/90 p-8 shadow-frame backdrop-blur-sm">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: EASE }}
                        className="overflow-hidden rounded-lg border border-black/[0.08] bg-charcoal/[0.04] px-4 py-3 text-sm text-charcoal"
                      >
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div>
                    <label
                      htmlFor="email"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-lg border border-black/[0.08] bg-white px-4 py-3 text-sm text-charcoal placeholder-ink-muted/60 transition-colors focus:border-charcoal/30 focus:outline-none focus:ring-2 focus:ring-charcoal/10"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary mt-1 w-full disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                  >
                    {loading ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>
              </div>

              <p className="mt-6 text-center text-sm text-ink-muted">
                Remember your password?{' '}
                <Link
                  to="/signin"
                  className="font-semibold text-charcoal transition-colors hover:text-ink-muted"
                >
                  Sign in
                </Link>
              </p>
            </>
          )}
        </motion.div>
      </div>
    </MotionConfig>
  );
};

export default ForgotPassword;
